import React from 'react';
import { Power, Info, TrendingUp, Zap } from 'lucide-react';
import { formatMWK } from './tokens';

/**
 * Screen 1 — center overlay shown when driver is OFFLINE.
 * Big pill "Go Online" + estimated next-4hr earnings + demand & surge chips.
 */
const OfflineCenterCard = ({
  estimatedNext4h = 0,
  demandLevel = 'medium',  // low | medium | high
  surgeActive = false,
  onGoOnline,
  disabled = false,
  disabledReason = '',
}) => {
  const demandColor = {
    high: 'bg-emerald-50 text-emerald-700',
    medium: 'bg-amber-50 text-amber-700',
    low: 'bg-gray-100 text-gray-600',
  }[demandLevel] || 'bg-gray-100 text-gray-600';
  const demandLabel = { high: 'High', medium: 'Medium', low: 'Low' }[demandLevel] || 'Low';

  return (
    <div
      data-testid="driver-offline-card"
      className="bg-white/95 backdrop-blur-md rounded-3xl p-5 shadow-[0_12px_40px_rgba(0,0,0,0.18)] border border-gray-100"
    >
      <button
        type="button"
        onClick={onGoOnline}
        disabled={disabled}
        data-testid="driver-go-online-btn"
        className={`w-full h-16 rounded-full text-white text-lg font-semibold tracking-wide flex items-center justify-center gap-2.5 transition-all active:scale-[0.98] shadow-lg ${
          disabled
            ? 'bg-gray-300 cursor-not-allowed shadow-none'
            : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/30'
        }`}
        aria-label={disabled ? disabledReason : 'Go online'}
      >
        <Power className="w-5 h-5" strokeWidth={2.5} />
        Go Online
      </button>

      {disabled && disabledReason ? (
        <p className="mt-2.5 text-center text-xs text-amber-700 bg-amber-50 rounded-lg py-1.5 px-2.5" data-testid="driver-disabled-reason">
          {disabledReason}
        </p>
      ) : null}

      <div className="mt-4 flex items-start gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Est. earnings · next 4 hrs
            <Info className="w-3 h-3 text-gray-400" aria-label="Based on local demand and your last 7 days" />
          </div>
          <p className="text-[26px] font-bold text-gray-900 leading-tight tabular-nums" data-testid="driver-est-next4h">
            {formatMWK(estimatedNext4h)}
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <span
          data-testid="driver-demand-chip"
          className={`flex-1 inline-flex items-center justify-center gap-1.5 ${demandColor} rounded-xl py-2 text-xs font-semibold`}
        >
          <TrendingUp className="w-3.5 h-3.5" />
          Demand: {demandLabel}
        </span>
        <span
          data-testid="driver-surge-chip"
          className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-semibold ${
            surgeActive ? 'bg-orange-50 text-orange-700' : 'bg-gray-100 text-gray-500'
          }`}
        >
          <Zap className="w-3.5 h-3.5" />
          Surge: {surgeActive ? 'Yes' : 'No'}
        </span>
      </div>
    </div>
  );
};

export default OfflineCenterCard;
